import { Link } from "wouter"
import { ArrowRight, MessageSquare, BarChart2, Zap, Users, ShieldCheck, Star, Lock } from "lucide-react"
import { coachPersonas } from "../lib/personas"
import PricingModal from "../components/PricingModal"
import { useState } from "react"


const features = [
  {
    name: "Personalized Coaching",
    description: "Coaching that adapts to what you need, how you think, and how you want to be supported.",
    icon: MessageSquare,
  },
  {
    name: "Growth Analytics",
    description: "See patterns, themes, and areas for growth through a clear, easy‑to‑understand view.",
    icon: BarChart2,
  },
  {
    name: "Instant Support",
    description: "On‑demand coaching available whenever you need it—no scheduling required.",
    icon: Zap,
  },
  {
    name: "Diverse Coaches",
    description: "Choose from six distinct coaches, each designed to support a different coaching need.",
    icon: Users,
  },
  {
    name: "Private by Design",
    description: "Secure and private environment with full user control over their data.",
    icon: Lock,
  },
]

export default function LandingV2() {
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);

  return (
    <div className="bg-background text-foreground font-sans min-h-screen flex flex-col">
      {/* Beta Badge - Fixed top-right corner */}
      <div className="fixed top-4 right-4 z-50 bg-primary/90 text-white px-3 py-1 rounded-lg text-xs font-semibold backdrop-blur-sm shadow-sm">
        Beta
      </div>
      <header className="sticky top-0 bg-white/80 backdrop-blur-lg z-10 border-b">
        <div className="container mx-auto px-4 h-20 flex justify-between items-center">
          <Link
            href="/"
            className="flex items-center gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md"
          >
            <img src="/hipo-logo.png" alt="HiPo AI Coach logo" className="h-8 w-auto" />
            <span className="font-display text-2xl font-bold text-primary">HiPo AI Coach</span>
          </Link>
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#coaches" className="relative text-slate-600 hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-full after:bg-primary after:scale-x-0 hover:after:scale-x-100 after:origin-left after:transition-transform">Coaches</a>
            <a href="#features" className="relative text-slate-600 hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-full after:bg-primary after:scale-x-0 hover:after:scale-x-100 after:origin-left after:transition-transform">Features</a>
            <button 
              onClick={() => setIsPricingModalOpen(true)}
              className="relative text-slate-600 hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-full after:bg-primary after:scale-x-0 hover:after:scale-x-100 after:origin-left after:transition-transform"
            >
              Pricing
            </button>
          </nav>
          <div className="flex items-center space-x-4">
            <Link
              href="/login"
              className="text-slate-600 hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md"
            >
              Login
            </Link>
            <Link
              href="/signup"
              className="bg-primary text-primary-foreground px-5 py-2.5 rounded-lg font-semibold hover:bg-primary/90 transition-colors transition-transform hover:scale-105"
            >
              Get Started
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <section className="container mx-auto px-4 pt-20 pb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-in fade-in-50 duration-700">
              <h1 className="font-display text-5xl md:text-6xl font-extrabold leading-tight text-slate-900">
                5+1 Coaches. Advantage You.
              </h1>
              <p className="mt-6 text-lg md:text-xl text-slate-600 max-w-xl">
                From leadership to life—choose the right coach for every challenge and build momentum with private, on‑demand guidance. Confidential by default, no data sharing or transcript storage. 
              </p>
              <p className="mt-3 text-sm text-slate-500 max-w-xl">
                Welcome to our beta! Tell us what you think and let's build this product together.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  href="/signup"
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-transform hover:scale-105"
                >
                  Start Free
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a href="#how" className="inline-flex items-center gap-2 border border-input bg-background text-foreground px-6 py-3 rounded-lg font-semibold hover:bg-accent/10 transition-colors">
                  See How It Works
                </a>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-green-500" /> Private & Secure</div>
                <div className="flex items-center gap-2"><Star className="h-5 w-5 text-yellow-500" /> Loved by Professionals</div>
              </div>
            </div>
            <div className="relative animate-in fade-in-50 duration-700 delay-100">
              <div className="relative aspect-[4/3] rounded-3xl bg-gradient-to-br from-primary/20 via-accent/15 to-transparent ring-1 ring-border shadow-sm flex items-center justify-center overflow-hidden">
                <img src="/hipo-logo.png" alt="" aria-hidden="true" className="h-40 w-auto opacity-90" />
              </div>
            </div>
          </div>
        </section>

        <section id="coaches" className="container mx-auto px-4 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-display text-4xl font-bold text-slate-900">Meet Your Coaching Team</h2>
            <p className="mt-4 text-slate-600">Choose the persona that matches your goals and style.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {coachPersonas.map((p) => (
              <div key={p.id} className="p-6 rounded-2xl border bg-white hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className="w-16 h-16 rounded-full overflow-hidden ring-1 ring-border">
                  <img
                    src={`/coach-avatars/${p.avatar}`}
                    alt={`${p.name} avatar`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="mt-4 text-xl font-bold text-slate-900">{p.name}</h3>
                <p className="text-sm text-slate-600">{p.title}</p>
                <p className="mt-2 text-sm text-slate-600">{p.bio}</p>
                <Link
                  href="/signup"
                  className="mt-6 inline-flex items-center gap-2 text-primary font-semibold hover:underline"
                >
                  Start with {p.name}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section id="features" className="py-20 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="font-display text-4xl font-bold text-slate-900">A Clear Path to Better Performance</h2>
              <p className="mt-4 text-lg text-slate-600">A structured experience that makes personal growth feel effortless.</p>
            </div>
            <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-5 gap-8 animate-in fade-in-50 duration-700">
              {features.map((f) => (
                <div key={f.name} className="p-6 bg-white rounded-xl border transition-all hover:shadow-md hover:-translate-y-0.5">
                  <div className="bg-primary/10 text-primary w-12 h-12 rounded-lg flex items-center justify-center">
                    <f.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 text-xl font-bold text-slate-900">{f.name}</h3>
                  <p className="mt-2 text-slate-600">{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        

        <section id="how" className="container mx-auto px-4 py-20">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 rounded-xl border bg-white">
              <div className="text-sm font-semibold text-primary">Step 1</div>
              <h3 className="mt-2 text-xl font-bold">Pick Your Coach</h3>
              <p className="mt-2 text-slate-600">Choose a persona that fits your goal and vibe.</p>
            </div>
            <div className="p-6 rounded-xl border bg-white">
              <div className="text-sm font-semibold text-primary">Step 2</div>
              <h3 className="mt-2 text-xl font-bold">Start a Conversation</h3>
              <p className="mt-2 text-slate-600">Ask anything, anytime. It’s fast, friendly, and focused on you.</p>
            </div>
            <div className="p-6 rounded-xl border bg-white">
              <div className="text-sm font-semibold text-primary">Step 3</div>
              <h3 className="mt-2 text-xl font-bold">See Your Progress</h3>
              <p className="mt-2 text-slate-600">Track insights and accelerate your growth.</p>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4 text-center">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Ready to elevate your work?</h2>
            <p className="mt-3 text-slate-600">Join professionals who trust HiPo for coaching every day.</p>
            <div className="mt-8 flex justify-center gap-4">
              <Link
                href="/signup"
                className="bg-primary text-primary-foreground px-8 py-4 rounded-lg font-semibold hover:bg-primary/90"
              >
                Start Free
              </Link>
              <Link
                href="/login"
                className="border border-input bg-background text-foreground px-8 py-4 rounded-lg font-semibold hover:bg-accent/10"
              >
                Log In
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Mobile sticky CTA bar */}
      <div className="fixed bottom-0 left-0 right-0 z-20 border-t bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/60 md:hidden">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-medium">Start your coaching journey</span>
          <Link
            href="/signup"
            className="bg-primary text-primary-foreground px-4 py-2 rounded-md font-semibold"
          >
            Start Free
          </Link>
        </div>
      </div>

      {/* Pricing Modal */}
      <PricingModal 
        isOpen={isPricingModalOpen} 
        onClose={() => setIsPricingModalOpen(false)} 
      />
    </div>
  )
}
